<template>
    <div class="flex-1 flex flex-col overflow-hidden min-h-0">
        <div class="shrink-0 bg-white border-b border-gray-100 px-6 py-4">
            <div class="max-w-3xl mx-auto flex items-center justify-between">
                <div>
                    <h1 class="title-body-bold text-gray-900">IMPACT Leader Code Self-Reflection</h1>
                    <p class="body-small text-gray-500 mt-1">Your results</p>
                </div>
            </div>
        </div>
        <div class="flex-1 overflow-y-auto">
            <div class="max-w-3xl mx-auto px-6 py-8 space-y-6">
                <div class="grid gap-4" :class="coaching_session_url && gather_feedback_url ? 'grid-cols-2' : 'grid-cols-1'">
                    <div v-if="coaching_session_url" class="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex flex-col gap-4">
                        <div class="w-9 h-9 rounded-lg bg-valence-purple/10 flex items-center justify-center shrink-0">
                            <i class="bi bi-stars text-valence-purple text-sm" aria-hidden="true"></i>
                        </div>
                        <div class="flex-1">
                            <p class="body-bold text-gray-900 mb-1">Coach with Nadia</p>
                            <p class="body-small text-gray-500">Turn your results into a focused development plan with your AI coach.</p>
                        </div>
                        <a
                            :href="coaching_session_url"
                            class="w-full text-center py-2.5 rounded-xl body-small-bold bg-valence-purple text-white hover:bg-valence-purple/90 transition-colors">
                            {{ isNewlySubmitted ? "Start coaching →" : "Continue chat →" }}
                        </a>
                    </div>

                    <div v-if="gather_feedback_url" class="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex flex-col gap-4">
                        <div class="w-9 h-9 rounded-lg bg-valence-purple/10 flex items-center justify-center shrink-0">
                            <i class="bi bi-people text-valence-purple text-sm" aria-hidden="true"></i>
                        </div>
                        <div class="flex-1">
                            <p class="body-bold text-gray-900 mb-1">Gather peer feedback</p>
                            <p class="body-small text-gray-500">See how others experience your leadership through anonymous peer feedback.</p>
                        </div>
                        <a
                            :href="gather_feedback_url"
                            class="w-full text-center py-2.5 rounded-xl body-small-bold border border-valence-purple text-valence-purple hover:bg-valence-purple/5 transition-colors">
                            Gather feedback →
                        </a>
                    </div>
                </div>

                <div class="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                    <div class="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
                        <div class="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center shrink-0">
                            <i class="bi bi-arrow-up-circle text-green-600 text-sm" aria-hidden="true"></i>
                        </div>
                        <h3 class="body-bold text-gray-900">Your Greatest Strengths</h3>
                        <span class="details text-gray-400 ml-auto">Rated 4–5</span>
                    </div>
                    <div v-if="completedStrengths.length" class="divide-y divide-gray-50">
                        <div v-for="q in completedStrengths" :key="q.index" class="px-6 py-3 flex items-center justify-between gap-4">
                            <div>
                                <span class="body-small-semibold text-gray-800">{{ q.title }}</span>
                                <span class="details text-gray-400 ml-2">{{ q.competency }}</span>
                            </div>
                            <div class="shrink-0 details px-2.5 py-1 rounded-full bg-green-100 text-green-700">
                                {{ completed_responses?.[q.index] ?? 0 }} · {{ rating_labels[String(completed_responses?.[q.index] ?? 0)] }}
                            </div>
                        </div>
                    </div>
                    <div v-else class="px-6 py-4 body-small text-gray-400">No behaviors rated 4 or above.</div>
                </div>

                <div class="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                    <div class="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
                        <div class="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
                            <i class="bi bi-arrow-up-right-circle text-amber-600 text-sm" aria-hidden="true"></i>
                        </div>
                        <h3 class="body-bold text-gray-900">Areas of Opportunity</h3>
                        <span class="details text-gray-400 ml-auto">Rated 1–2</span>
                    </div>
                    <div v-if="completedOpportunities.length" class="divide-y divide-gray-50">
                        <div v-for="q in completedOpportunities" :key="q.index" class="px-6 py-3 flex items-center justify-between gap-4">
                            <div>
                                <span class="body-small-semibold text-gray-800">{{ q.title }}</span>
                                <span class="details text-gray-400 ml-2">{{ q.competency }}</span>
                            </div>
                            <div class="shrink-0 details px-2.5 py-1 rounded-full bg-amber-100 text-amber-700">
                                {{ completed_responses?.[q.index] ?? 0 }} · {{ rating_labels[String(completed_responses?.[q.index] ?? 0)] }}
                            </div>
                        </div>
                    </div>
                    <div v-else class="px-6 py-4 body-small text-gray-400">No behaviors rated 2 or below.</div>
                </div>

                <div class="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                    <div class="px-6 py-4 border-b border-gray-100">
                        <h3 class="body-bold text-gray-900">Full Breakdown</h3>
                    </div>
                    <div v-for="(group, i) in groupedQuestions" :key="i" class="border-b border-gray-50 last:border-0">
                        <div class="px-6 py-2.5 bg-gray-50">
                            <!-- One-off: 12px font-bold tracking-widest eyebrow; no exact utility match -->
                            <span class="details font-bold tracking-widest text-valence-purple uppercase">{{ group.competency }}</span>
                        </div>
                        <div v-for="q in group.questions" :key="q.index" class="px-6 py-2.5 flex items-center justify-between gap-4 border-t border-gray-50 first:border-0">
                            <span class="body-small text-gray-600">{{ q.title }}</span>
                            <div
                                :class="[
                                    'shrink-0 details px-2.5 py-1 rounded-full',
                                    (completed_responses?.[q.index] ?? 0) === 0
                                        ? 'bg-gray-100 text-gray-400'
                                        : (completed_responses?.[q.index] ?? 0) >= 4
                                          ? 'bg-green-100 text-green-700'
                                          : 'bg-amber-100 text-amber-700',
                                ]">
                                {{
                                    (completed_responses?.[q.index] ?? 0) === 0
                                        ? "—"
                                        : `${completed_responses?.[q.index] ?? 0} · ${rating_labels[String(completed_responses?.[q.index] ?? 0)]}`
                                }}
                            </div>
                        </div>
                    </div>
                </div>

                <div class="text-center pb-4">
                    <template v-if="!confirmingRetake">
                        <button class="body-small text-gray-400 hover:text-gray-600 underline transition-colors" @click="emit('start-retake')">Retake self-reflection</button>
                    </template>
                    <template v-else>
                        <p class="body-small text-gray-600 mb-2">This will clear your previous results. Are you sure?</p>
                        <p v-if="retakeError" class="details text-amber-600 mb-2">{{ retakeError }}</p>
                        <div class="flex justify-center gap-3">
                            <button
                                class="px-4 py-1.5 rounded-lg body-small-bold bg-red-600 text-white hover:bg-red-700 transition-colors"
                                :disabled="isRetaking"
                                @click="emit('confirm-retake')">
                                <span v-if="isRetaking">Resetting...</span>
                                <span v-else>Yes, retake</span>
                            </button>
                            <button
                                class="px-4 py-1.5 rounded-lg body-small text-gray-500 hover:text-gray-700 border border-gray-300 transition-colors"
                                :disabled="isRetaking"
                                @click="emit('cancel-retake')">
                                Cancel
                            </button>
                        </div>
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
interface Question {
    index: number;
    competency: string;
    title: string;
    description: string;
}

interface QuestionGroup {
    competency: string;
    definition: string;
    questions: Question[];
}

interface Props {
    coaching_session_url?: string | null;
    gather_feedback_url?: string | null;
    completedStrengths: Question[];
    completedOpportunities: Question[];
    groupedQuestions: QuestionGroup[];
    completed_responses?: Record<number, number>;
    rating_labels: Record<string, string>;
    confirmingRetake: boolean;
    isRetaking: boolean;
    retakeError: string | null;
    isNewlySubmitted: boolean;
}

defineProps<Props>();

const emit = defineEmits<{
    (e: "start-retake"): void;
    (e: "cancel-retake"): void;
    (e: "confirm-retake"): void;
}>();
</script>
